import { FC, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FullPageLoader, Loader } from '@components';
import AccountingRevenueFilters from '../components/AccountingRevenue/AccountingRevenueFilters';
import FinancialProjectionsTable from '../components/FinancialProjections/FinancialProjectionsTable/FinancialProjectionsTable';
import AccountingRevenueContent from '../components/AccountingRevenue/AccountingRevenueContent';
import useCharts from '@hooks/api/useCharts';
import useChartById from '@hooks/api/useChartById';
import { processCombinedCohortData } from '../components/FinancialProjections/FinancialProjectionsTable/tableDataUtil';

const rowOrder = [
  'Average Exposure (Deal)',
  'Gross Accounting Revenue',
  'Average Exposure (Investor)',
  'Cost of Capital',
  'Net Accounting Revenue / Gross Margin',
];

const AccountingRevenue: FC<{ id: string }> = ({ id }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [dashboardLoader, setDashboardLoader] = useState<boolean>(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [componentLoader, setComponentLoader] = useState<boolean>(false);

  const { getChartData } = useCharts();
  const { getChartDataById } = useChartById();

  const formatChartResponseData = (response: any) => {
    let columns = [];
    let rows = [];
    const metadata = response.metadata;
    const { mappedColumns, mappedData } = processCombinedCohortData(
      response.name,
      metadata,
      response.result,
    );
    columns = mappedColumns;
    rows = mappedData;
    rows.sort((a, b) => a.rowLabel.localeCompare(b.rowLabel));
    return { rows, columns, metadata };
  };

  const fetchDashboardData = async (filters?: Record<string, string>) => {
    try {
      if (!componentLoader) {
        setDashboardLoader(true);
      }
      const chartsById: any[] = [];
      const tabsChartsList = await getChartData(id);

      await Promise.all(
        tabsChartsList.map(async (chart: any) => {
          const parsedMetadata = JSON.parse(chart.metadata);
          const response = await getChartDataById(
            id,
            chart.id,
            filters,
            parsedMetadata?.sort?.fields,
            parsedMetadata?.sort?.order,
          );
          if (response) {
            const { rows, columns, metadata } = formatChartResponseData(response);
            chartsById.push({
              columns,
              rows,
              id: chart.id,
              name: response.name,
              meta: metadata,
              rowOrder: response.rowOrder,
            });
          }
        }),
      );

      chartsById.sort((a, b) => a.rowOrder - b.rowOrder);
      setChartData(chartsById);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setDashboardLoader(false);
      setComponentLoader(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(filters);
  }, [filters]);

  const handleFilterChange = (newSelections: Record<string, string>) => {
    setComponentLoader(true);
    setFilters(newSelections);
  };

  const sortedRows = rowOrder
    .map((key: any) => chartData?.[0]?.rows.find((row: any) => row.rowLabel === key))
    .filter(Boolean);

  const customColumns =
    chartData?.[0]?.columns?.map((col: any) => ({
      ...col,
      renderHeader: (column: any) => {
        if (column.key === 'rowLabel') {
          return (
            <Box
              sx={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              {column.label}
              <ArrowForwardIcon sx={{ color: '#9AD6D1' }} />
            </Box>
          );
        }
        return column.label;
      },
    })) || [];

  return (
    <>
      {dashboardLoader && <FullPageLoader hasSideNav={true} />}
      <div
        id="pvx-dashboard-container"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          overflow: 'auto',
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingTop: '1rem',
          paddingBottom: isMobile ? '5rem' : '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            fontSize={'2rem'}
            fontWeight={600}
            sx={{ color: '#1B1C17', mt: '0.5rem', mb: '0.5rem' }}
          >
            Accounting Revenue
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: '#6C6C6C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '4.688rem',
            }}
          >
            $ = USD
          </Typography>
        </Box>
        <AccountingRevenueFilters id={id} onFilterChange={handleFilterChange} />
        <Box
          sx={{
            width: '100%',
            padding: '0.875rem',
            backgroundColor: '#FFF',
            borderRadius: '0.75rem',
          }}
        >
          {componentLoader ? (
            <Box
              sx={{
                width: '100%',
                height: '10rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Loader />
            </Box>
          ) : (
            <FinancialProjectionsTable
              data={sortedRows}
              columns={customColumns}
              meta={chartData?.[0]?.meta}
              handleFieldSave={() => {}}
            />
          )}
        </Box>
        <AccountingRevenueContent />
      </div>
    </>
  );
};

export default AccountingRevenue;
